import type { ApplicantPromisePublic } from '@/components/domain/promise/promisePublicModel';
import type { RecruitmentSkillView, SkillLimits, SkillMatch, SkillMeta } from '@/components/domain/recruitment/skills/skillModel';


export interface ListResponse<T> {
    list: T[];
    total: number;
    totalElements?: number;
    /** 지원 내역(/applications) 등 일부 API 는 전체 건수를 이 이름으로 내려준다 */
    totalCount?: number;
    offset: number;
    limit: number;
}

export interface ValidateResponse<T> {
    code: number;
    message: string;
    result: T;
}

/** 비슷한 공고 목록 조건 — `GET /recruitments/{id}/similar-query` */
export interface SimilarRecruitmentQuery {
    /** 채용 목록 주소 쿼리 — `category=REGION&districts=…&recruitmentTypes=…&jobTasks=…` */
    query: string;
    /** 그 조건의 공고 수(넓혀도 0건이면 0) */
    total: number;
    /** 고른 단계 — 구+형태+업무 → 구+형태 → 시+형태+업무 → 시+형태 → 전국+형태+업무 */
    level: 'DISTRICT_TYPE_TASK' | 'DISTRICT_TYPE' | 'CITY_TYPE_TASK' | 'CITY_TYPE' | 'NATIONWIDE_TYPE_TASK';
}

/**
 * 내 정보 수정 페이지의 약관 동의 상태 응답.
 * 필수 4 + 선택(이력서 공개/맞춤 추천/브리핑 채널) + 약관 본문 버전 + 마지막 변경 시각.
 * 앱 푸시 동의는 PushAgreementsResponse 로 분리.
 */
export interface UserAgreementResponse {
    termsVersion: string | null;
    service: boolean;
    privacy: boolean;
    thirdParty: boolean;
    sensitive: boolean;
    resumePublic: boolean;
    recommendation: boolean;
    briefEmail: boolean;
    briefKakao: boolean;
    briefEmailAt: string | null;
    briefKakaoAt: string | null;
    updateAt: string | null;
}

/**
 * 선택 약관 변경 요청 — 전달된 키만 변경한다.
 */
export interface UpdateOptionalAgreementsPayload {
    resumePublic?: boolean;
    recommendation?: boolean;
    briefEmail?: boolean;
    briefKakao?: boolean;
}

/**
 * 푸시 설정 응답 — 광고성(ADVERTISING) / 서비스(SERVICE) 그룹별 토픽 목록.
 * 항목·기본값은 서버(core PushTopic)가 결정하고 프론트는 그대로 그린다.
 */
export interface PushAgreementItem {
    topic: string;
    title: string;
    description: string;
    agreed: boolean;
    agreedAt: string | null;
    /** 끌 수 없는 주제(약속 기록) — 화면은 켜짐 고정(회색) */
    locked?: boolean;
}

export interface PushAgreementGroup {
    category: 'ADVERTISING' | 'SERVICE';
    title: string;
    description: string;
    items: PushAgreementItem[];
}

/** 알림함 항목 (30일 보관). category 는 데이터 분류용 — 화면 탭은 아직 없음. */
export interface NotificationItem {
    id: string;
    type: string;
    category: 'ACTIVITY' | 'RECRUIT' | 'SEMINAR' | 'NEWS';
    title: string;
    body: string;
    link: string | null;
    read: boolean;
    createdAt: string;
}

export interface NotificationPage {
    list: NotificationItem[];
    total: number;
    page: number;
    size: number;
}

export interface PushAgreementsResponse {
    groups: PushAgreementGroup[];
}

/** 목록에서 저장한 조건 한 건 — query 는 목록 주소 쿼리(정렬·검색어 제외), notify 는 조건에 맞는 새 글 알림 */
export interface SavedFilterResponse {
    query: string;
    notify: boolean;
    updatedAt: string;
}

/** GET /users/me/saved-filters — 목록마다 저장한 조건(없으면 null) */
export interface SavedFiltersResponse {
    recruitment: SavedFilterResponse | null;
    seminar: SavedFilterResponse | null;
}

/**
 * 면허 인증 상태.
 * - NONE: 신청 이력 없음 / PENDING: 검수 대기 / APPROVED: 승인(배지 부여)
 * - REJECTED: 반려(사유 표시 후 재신청) / EXPIRED: 검수 전 30일 경과로 자동 파기(재신청 필요)
 */
export type LicenseVerificationStatus = 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

/**
 * GET /users/me/license-verification 응답.
 * status 가 NONE 이면 name·birthDate·licenseNumber 는 프로필 값이 프리필로 내려온다.
 */
export interface LicenseVerificationResponse {
    status: LicenseVerificationStatus;
    submittedAt?: string | null;
    reviewedAt?: string | null;
    rejectReason?: string | null;
    name: string | null;
    /** yyyy-MM-dd */
    birthDate: string | null;
    licenseNumber: string | null;
    hasImage: boolean;
}

/** POST /users/me/license-verification 요청 (multipart 로 전송) */
export interface LicenseVerificationSubmitPayload {
    name: string;
    /** yyyy-MM-dd */
    birthDate: string;
    licenseNumber: string;
    /** 필수 동의 — 서버에는 'true' 문자열로 전송 */
    consent: boolean;
    /** 필수 — jpg/png/pdf, 10MB 이하 (없으면 서버 400) */
    image?: File | null;
    /** 자동 판독 제외 (국외 이전 거부). 기본 false */
    autoCheckOptOut?: boolean;
}

/** POST /users/me/license-verification 성공 응답 */
export interface LicenseVerificationSubmitResult {
    status: LicenseVerificationStatus;
    submittedAt: string;
}

/* ===== 급여 실태조사 ===== */

/** 치과 규모(체어 수) */
export type SalaryChairBand = 'LE3' | 'B4_6' | 'B7_10' | 'GE11';
/** 근무 형태 */
export type SalaryEmploymentType = 'FULL' | 'CONTRACT' | 'PART';
/** 주 근무일 */
export type SalaryWorkDays = 'D4' | 'D4_5' | 'D5' | 'D5_5_PLUS';
/** 주 근무시간 */
export type SalaryWorkHoursBand = 'LE35' | 'H36_40' | 'H41_45' | 'GE46';
/** 급여 형태 */
export type SalaryPayType = 'ANNUAL' | 'MONTHLY' | 'HOURLY';
/** 4대보험·퇴직금 */
export type SalaryInsuranceType = 'BOTH' | 'INSURANCE_ONLY' | 'NONE';
/** 이직 시 최우선 조건 */
export type SalaryTopPriority = 'PAY' | 'HOURS' | 'DISTANCE' | 'CULTURE' | 'GROWTH';

/** 회차(캠페인) — key 는 `yyyy-MM` */
export interface SalarySurveyCampaign {
    key: string;
    title: string;
    opensAt: string;
    closesAt: string;
    publishAt: string;
    status: 'OPEN' | 'PUBLISHED';
    publishedAt: string | null;
}

/** 랜딩 티저 (직전 발행 리포트 기준, 없으면 null) */
export interface SalarySurveyTeaser {
    label: string;
    value: string;
    campaignKey: string;
}

/** 이번 회차에 저장된 내 응답 (설문 폼 프리필·수정 모드) */
export interface SalarySurveyMyResponse {
    campaignKey: string;
    userType: string;
    careerYear: number;
    careerBand: string;
    cityName: string;
    districtId: string | null;
    districtName: string | null;
    chairBand: SalaryChairBand;
    employmentType: SalaryEmploymentType;
    workDays: SalaryWorkDays;
    workHoursBand: SalaryWorkHoursBand;
    payType: SalaryPayType;
    monthlyNetPay: number | null;
    payBand: number | null;
    incentiveYn: boolean;
    incentiveMonthly: number | null;
    insurance: SalaryInsuranceType;
    leaveScore: number;
    turnoverScore: number;
    topPriority: SalaryTopPriority;
    comment: string | null;
    updatedAt: string;
}

/** 개인 결과를 못 보는 사유 — 면허 인증 전 / 치과위생사 회원이 아님 */
export type SalarySurveyPersonalResultBlockedReason = 'LICENSE_REQUIRED' | 'HYGIENIST_ONLY';

/** 공개 리포트 상세 잠금 사유 — 비로그인 / 면허 미인증 / 치과위생사 아님 */
export type SalaryReportDetailLockedReason = 'LOGIN_REQUIRED' | 'LICENSE_REQUIRED' | 'HYGIENIST_ONLY' | 'SURVEY_REQUIRED';

/** GET /salary-surveys/campaigns/current 응답 (비로그인은 개인 필드가 비어 있다) */
export interface SalarySurveyCurrentResponse {
    campaign: SalarySurveyCampaign;
    /** total = 이번 회차 참여 수, verified = 집계 대상(면허 인증 치과위생사) 응답 수, thisYear = 올해 참여 수(발행 집계와 같은 모수) */
    counts: { total: number; verified: number; thisYear?: number };
    teaser: SalarySurveyTeaser | null;
    myResponse: SalarySurveyMyResponse | null;
    myGroupCount: number | null;
    licenseStatus: LicenseVerificationStatus;
    myResultAvailable: boolean;
    /** 개인 결과를 받을 자격이 있는 회원인지 (결과 계산 여부와는 별개, 비로그인은 false) */
    eligibleForPersonalResult: boolean;
    /** 자격이 없을 때의 사유 — 자격이 있거나 비로그인이면 null */
    personalResultBlockedReason: SalarySurveyPersonalResultBlockedReason | null;
    /** 비교에 쓰는 전년 기준선 연도 (자료가 없으면 null) */
    baselineYear: number | null;
    /** myGroupCount 를 센 묶음 이름 — "서울 강남구 · 치과위생사 · 3~4년" (발행과 같은 규칙) */
    myGroupLabel?: string | null;
    /** 올해(1~12월) 응답이 있는지 — 있으면 새 참여 대신 myLatestResponse 를 고친다(한 해에 한 번 참여). 옛 서버는 없음 */
    respondedThisYear?: boolean;
    /** 올해 내 최신 응답(회차 무관) — 응답 고치기 화면이 미리 채운다 */
    myLatestResponse?: SalarySurveyMyResponse | null;
    /** 다시 새로 참여할 수 있는 시각(다음 해 1월 1일) */
    nextEligibleAt?: string | null;
    /** 내가 실제로 응답한 최신 회차 키·제목(기간 무관) — 내 급여 위치 상태 화면 부제·리포트 링크용 */
    latestResponseCampaignKey?: string | null;
    latestResponseCampaignTitle?: string | null;
    /** 가장 최근 발행 리포트 회차 키 */
    latestReportKey?: string | null;
    /** 아직 보지 않은 새 리포트가 있는지 (마이페이지 N). 비로그인은 false */
    hasUnseenReport?: boolean;
}

/**
 * PUT /salary-surveys/campaigns/{key}/responses/me 요청 (13문항).
 * 실수령은 직접 입력([monthlyNetPay])이 원칙이고, 모르면 구간([payBand], 50만 원 단위 하한)을 고른다 — 둘 중 하나 필수.
 */
export interface SalarySurveyResponsePayload {
    userType: string;
    careerYear: number;
    districtId?: string | null;
    cityName?: string | null;
    chairBand: SalaryChairBand;
    employmentType: SalaryEmploymentType;
    workDays: SalaryWorkDays;
    workHoursBand: SalaryWorkHoursBand;
    payType: SalaryPayType;
    monthlyNetPay?: number | null;
    payBand?: number | null;
    incentiveYn: boolean;
    incentiveMonthly?: number | null;
    insurance: SalaryInsuranceType;
    leaveScore: number;
    turnoverScore: number;
    topPriority: SalaryTopPriority;
    comment?: string | null;
    consent: boolean;
}

/** PUT /salary-surveys/campaigns/{key}/responses/me 응답 — 제출 직후 화면 재료 */
export interface SalarySurveySaveResult {
    status: 'SAVED';
    campaignKey: string;
    myGroupCount: number;
    totalCount: number;
    publishAt: string;
    /** 기존 응답을 고친 저장인지 — '고친 내용은 다음 발행부터 반영돼요' 안내 */
    revised?: boolean;
    /** myGroupCount 를 센 묶음 이름 */
    myGroupLabel?: string | null;
    /** 올해 참여 수 (발행 집계와 같은 모수) */
    yearCount?: number;
}

/**
 * 실수령 분포 한 칸 (25만 원 폭 9칸, `mine` 이 내 구간).
 * 양끝 칸(`openEnded`)은 그 아래·위 전부를 담으므로 `label` 이 열린 구간 표기("~200만", "375만~")로 온다.
 */
export interface SalaryHistogramBucket {
    from: number;
    to: number;
    count: number;
    mine: boolean;
    openEnded: boolean;
    label: string;
}

/** GET /salary-surveys/campaigns/{key}/results/me 응답 */
export interface SalarySurveyMyResult {
    campaignKey: string;
    /** 결과 회차 제목 — 예) "2026년 9~10월 급여 실태조사". 옛 서버·회차 문서 없음이면 없음/null */
    campaignTitle?: string | null;
    groupLabel: string;
    groupSize: number;
    fallbackLevel: number;
    myNetPay: number;
    percentileTop: number;
    median: number;
    q1: number;
    q3: number;
    histogram: SalaryHistogramBucket[];
    incentiveRate: number;
    fiveDayRate: number;
    turnoverIntentRate: number;
    /** 같은 회차 공개 리포트의 전국 비율(%) — 감춤·옛 리포트·옛 서버면 null/없음(화면은 비교 문구를 숨긴다) */
    nationalIncentiveRate?: number | null;
    nationalFiveDayRate?: number | null;
    nationalTurnoverIntentRate?: number | null;
    cityName: string | null;
    districtId: string | null;
    districtName: string | null;
    computedAt: string;
}

/** 개인 결과를 못 주는 이유 — 자격 없음은 200 본문(eligible=false), 결과 없음은 404 본문의 result */
export interface SalarySurveyMyResultUnavailable {
    /** false = 자격이 없어 못 본다(치과위생사 아님·면허 미인증). 옛 서버(403 본문)는 이 필드가 없다 */
    eligible?: boolean;
    reason: 'LICENSE_REQUIRED' | 'HYGIENIST_ONLY' | 'NOT_PUBLISHED_YET' | 'INSUFFICIENT_GROUP';
    licenseStatus?: LicenseVerificationStatus | null;
    publishAt?: string | null;
    /** 판정한 회차(내가 응답한 최신 회차)와 제목 — NOT_PUBLISHED_YET·INSUFFICIENT_GROUP 에서만 */
    campaignKey?: string | null;
    campaignTitle?: string | null;
}

/**
 * 200 응답이 결과 대신 "못 보는 이유"인지 — 자격이 없는 회원(치과위생사 아님·면허 미인증)은 403 이 아니라
 * 200 + `{ eligible: false, reason }` 으로 온다(2026-10-06). 결과 본문에는 reason 이 없다.
 */
export const isSalaryUnavailable = <T extends object>(
    result: T | SalarySurveyMyResultUnavailable | SalaryBaselineUnavailable | null | undefined,
): result is SalarySurveyMyResultUnavailable | SalaryBaselineUnavailable =>
    !!result && typeof (result as { reason?: unknown }).reason === 'string';

/** 기준선 비교 그룹의 근무 조건 비율 (%) */
export interface SalaryBaselineConditions {
    incentiveRate: number;
    hours36to40Rate: number;
    netContractRate: number;
}

/**
 * GET /salary-surveys/baselines/{year}/comparisons/me 응답 — "작년 조사 기준 내 위치".
 * 월간 결과와 달리 발행을 기다리지 않고 제출 직후 바로 계산된다.
 */
export interface SalaryBaselineComparison {
    year: number;
    /** 조사 시점 문구 — 예) "2026년 1월 연봉 설문" (출처 채널은 표기하지 않는다) */
    collectedLabel: string;
    /** 그 해 기준선 전체 응답 수 (배지) */
    totalCount: number;
    /** 그룹 폴백 단계 (0=지역×경력, 1=경력 전국, 2=전체) */
    level: number;
    groupLabel: string;
    groupSize: number;
    median: number;
    q1: number;
    q3: number;
    percentileTop: number;
    myNetPay: number;
    diffFromMedian: number;
    histogram: SalaryHistogramBucket[];
    conditions: SalaryBaselineConditions;
}

/** 기준선 비교를 못 주는 이유 — 자격 없음은 200 본문(eligible=false), 자료 없음은 404 본문의 result */
export interface SalaryBaselineUnavailable {
    eligible?: boolean;
    reason: 'LICENSE_REQUIRED' | 'HYGIENIST_ONLY' | 'NO_RESPONSE' | 'NO_BASELINE';
    licenseStatus?: LicenseVerificationStatus | null;
}

/** 경력 구간별 중간값 (5명 미만 셀은 목록에 없다) */
export interface SalaryCareerCell {
    band: string;
    label: string;
    median: number;
    count: number;
    /** 보통 범위(가운데 50%) — 응답 5명 미만이면 null */
    q1?: number | null;
    q3?: number | null;
}

/** 지역별 중간값 (3명 미만 셀은 목록에 없다) */
export interface SalaryRegionCell {
    cityName: string;
    median: number;
    count: number;
    /** 보통 범위(가운데 50%) — 응답 5명 미만이면 null */
    q1?: number | null;
    q3?: number | null;
}

/** 지역(시·도) × 경력 구간 중간값 */
export interface SalaryRegionCareerCell {
    cityName: string;
    band: string;
    label: string;
    median: number;
    count: number;
    /** 보통 범위(가운데 50%) — 응답 5명 미만이면 null */
    q1?: number | null;
    q3?: number | null;
}

/** 이직 시 최우선 조건 비율 */
export interface SalaryPriorityCell {
    key: string;
    label: string;
    ratio: number;
}

/** GET /salary-surveys/reports/{key} 응답 (공개) */
export interface SalarySurveyReport {
    campaignKey: string;
    title: string;
    publishedAt: string;
    respondentCount: number;
    /** 집계 기준이 '면허 인증 치과위생사 응답만' 인지 — 2026-11-01 이후 발행분은 항상 false(미인증 포함, 옛 발행분만 true 일 수 있다) */
    verifiedOnly: boolean;
    /** 상세(연차별·지역별·지역×연차·옮길 때 먼저 보는 것·연도별 비교)를 서버가 비웠는지 — 면허 인증 치과위생사만 상세를 받는다 */
    detailLocked?: boolean;
    /** 잠금 사유 — 잠기지 않았으면 null */
    detailLockedReason?: SalaryReportDetailLockedReason | null;
    nationalMedian: number | null;
    /** 전국 평균 — 감춤이면 null, 이 필드 도입 전 리포트는 없음 */
    nationalMean?: number | null;
    /** 전국 보통 범위(가운데 50%) */
    nationalQ1?: number | null;
    nationalQ3?: number | null;
    /** 집계 인원이 5명 미만이면 null (k-익명성) */
    turnoverIntentRate: number | null;
    /** 전국 인센티브 있음·주 5일 근무 비율(%) — 감춤이거나 이 필드 도입 전 리포트면 null/없음 */
    nationalIncentiveRate?: number | null;
    nationalFiveDayRate?: number | null;
    byCareer: SalaryCareerCell[];
    byRegion: SalaryRegionCell[];
    byRegionAll: SalaryRegionCell[];
    /** 지역 × 경력 구간 (3명 이상 칸만) — 이 필드 도입 전 발행된 리포트는 빈 배열 */
    byRegionCareer: SalaryRegionCareerCell[];
    priorities: SalaryPriorityCell[];
    periodFrom: string;
    periodTo: string;
    /** 지난 연초 조사 대비 — 기준선 임포트 전에 발행된 리포트는 null (화면에서 카드를 감춘다) */
    yearOverYear: SalaryYearOverYear | null;
}

/** 경력 구간별 기준선 대비 (우리 값이 5명 미만이면 ourMedian 은 null) */
export interface SalaryYearOverYearCareerCell {
    band: string;
    label: string;
    ourMedian: number | null;
    baselineMedian: number;
    baselineCount: number;
}

/** 권역별 기준선 대비 (확정 화면에서는 쓰지 않는다) */
export interface SalaryYearOverYearRegionCell {
    group: string;
    label: string;
    ourMedian: number | null;
    baselineMedian: number;
    baselineCount: number;
}

/** 연도별 전국 중간값 한 점 (연도 오름차순) */
export interface SalaryYearOverYearTrendPoint {
    year: number;
    nationalMedian: number;
    count: number;
}

/** 리포트의 "연도별 실수령 중간값" 재료 */
export interface SalaryYearOverYear {
    baselineYear: number;
    /** 배지·각주 문구 — 예) "연초 연봉 설문 2024~2026" */
    sourceLabel: string;
    baselineNationalMedian: number;
    byCareer: SalaryYearOverYearCareerCell[];
    byRegionGroup: SalaryYearOverYearRegionCell[];
    trend: SalaryYearOverYearTrendPoint[];
}

export interface SubwayStation {
    id: string;
    name: string;
    lineNo: string;
}

export interface SubwayArea {
    id: string;
    name: string;
}

export interface SubwayStationResponse {
    code: number;
    message: string;
    result: SubwayStation[];
}

export interface SignUpRequest {
    // 이메일이 로그인 식별자(아이디)다 — 별도의 id 필드는 더 이상 없다.
    password: string;
    name: string;
    nickname: string;
    /** 선택 입력 — 성별·생년월일·전화번호·주소는 핵심 기능과 무관하므로 필수가 아니다 */
    birthDate?: string;
    genderType?: 'MAN' | 'WOMAN';
    userType: string;
    /** 연차(필수, 신입 0) — 회원 정보에 저장한다 */
    careerYears?: number;
    licenseNumber?: string;
    phoneNumber?: string;
    email: string;
    authority: 'ROLE_USER';
    /** 호환용 — 서버는 addressCode 가 있으면 그것으로 지역을 판정한다 */
    districtId?: string;
    /** 주소의 카카오 b_code 원문(10자리) */
    addressCode?: string;
    stationIds: string[];
    address?: string;
    addressDetail?: string;
    signupVersion?: number;
    socialAccessToken?: string;
    socialProvider?: string;
    agreements?: Record<string, boolean>;
    termsVersion?: string;
}

export interface ClinicsSignUpRequest {
    signupVersion?: number;
    phoneNumber?: string;
    // 치과 계정 로그인 이메일(아이디). 담당자 이메일(clinic.managerEmail)과 별개.
    email: string;
    password: string;
    socialAccessToken?: string;
    socialProvider?: string;
    clinic: {
        name: string;
        since: string;
        phoneNumber: string;
        ownerName: string;
        managerName: string;
        managerEmail: string | undefined;
        managerPhoneNumber: string;
        description: string;
        totalWorkers: number;
        totalWorkersDescription: string;
        address: string;
        addressDetail: string;
        /** 호환용 — 서버는 addressCode 가 있으면 그것으로 지역을 판정한다 */
        districtId: string;
        /** 주소의 카카오 b_code 원문(10자리) */
        addressCode?: string;
        stationIds: string[];
        url: string;
        images?: string[];
        mobileImages?: string[];
        latitude: number;
        longitude: number;
        clinicType: string;
        /** 사업자등록번호 숫자 10자리(clinics.business_number) */
        businessNumber: string;
        /** 병원 인증에서 고른 병원 토큰 */
        hospitalCheckToken?: string;
        /** 병원 인증 단계의 사업자등록증 개업연월일 yyyyMMdd — 국세청 진위확인용 */
        ntsOpeningDate?: string;
        /** 병원 인증 — 홈페이지로 확인한 토큰 */
        homepageCheckToken?: string;
        /** 병원 인증 — 올린 사업자등록증 토큰 */
        businessCertToken?: string;
    };
    authority: 'ROLE_CLINIC';
    agreements?: Record<string, boolean>;
    termsVersion?: string;
}

export interface PostRequest {
    title: string;
    content: string;
    tag?: string;
    hashtags?: string[];
}

export interface CommentRequest {
    content: string;
    /** 붙인 사진 주소(속닥속닥 댓글, /upload/image 가 돌려준 주소). 수정 때 null 이면 사진을 뺀다 */
    imageUrl?: string | null;
}

/**
 * 속닥속닥 익명 이름(2026-10-11) — 치과 계정만 target=true 와 이름이 온다. 그 밖의 회원은 회원 닉네임으로 올라간다.
 * 서버: GET·PUT /api/posts/alias (ApiSokdakAliasResponse)
 */
export interface SokdakAlias {
    target: boolean;
    nickname?: string | null;
    changedAt?: string | null;
    /** 이 시각부터 바꿀 수 있다(null 이면 지금 바로) */
    nextChangeAt?: string | null;
    /** 바꿀 수 있을 때까지 남은 날(올림), 지금 바꿀 수 있으면 0 */
    remainingDays: number;
    minLength: number;
    maxLength: number;
    intervalDays: number;
}

export interface ReplyRequest {
    content: string;
    /** 답글에 다는 답글이면 그 답글 id — 대상 회원은 서버가 찾는다 */
    targetReplyId?: string;
    targetUserNickname?: string;
    /** 붙인 사진 주소(속닥속닥 답글). 수정 때 null 이면 사진을 뺀다 */
    imageUrl?: string | null;
}

export interface RecruitmentDetail {
    payAmountValue?: number;
    jobTasks: string[];
    certificates: string[];
    recruitmentTypes: string[];
    recruitment: string;
    payAmount?: string;
    payAmountType: string;
    position: string;
    careerTypes: string[];
    minCareer?: number;
    maxCareer?: number;
    academic?: string;
    academicEtc?: string;
    recruitmentDescription?: string;
}

export interface WelfareItem {
    type: string;
    description?: string;
}

export interface RecruimentCreationRequest {
    continuousRecruitment?: boolean;
    draftForm?: string;
    workPeriodType?: string;
    workWeekdays?: string[];
    weekdayNegotiable?: boolean;
    saturdayMode?: string | null;
    workDaysNote?: string;
    workStartDate?: string | null;
    workEndDate?: string | null;
    workDates?: string[];
    extraWelfare?: (WelfareItem & { title?: string })[];
    clinicId: number;
    title: string;
    recruitmentDetails: RecruitmentDetail[];
    emergency: boolean;
    /** 급여 지급 시점(SAME_DAY·WITHIN_DAYS·MONTHLY) — 없으면 null */
    payTiming?: string | null;
    /** 근무 후 N일 내·매월 N일의 N */
    payTimingDays?: number | null;
    /** 금액이 실수령(세후)이면 true */
    payAfterTax?: boolean;
    /** 세금·보험 코드 */
    payConditions?: string[];
    /** 특정 날짜의 날짜별 시간 */
    workDateSlots?: { date: string; start: string | null; end: string | null; hourlyPay: number | null }[];
    /** 요일 선택·기간 지정의 요일별 시간 */
    workWeekdaySlots?: { weekday: string; start: string | null; end: string | null }[];
    startDate?: string | null;
    endDate?: string | null;
    workingHours?: string | null;
    workingDaysType?: string | null;
    workingDays?: string;
    livingWelfare?: WelfareItem[];
    familyEventWelfare?: WelfareItem[];
    educationWelfare?: WelfareItem[];
    rewardWelfare?: WelfareItem[];
    etcConditions?: string;
    images?: string[];
    mobileImages?: string[];
    applyDocument?: string[];
    applyMethod?: string[];
    screening?: string | null;
    status?: string;
    /** 업무 능력 — 새 화면은 항상 보낸다(빈 배열이면 지움, 칸이 없으면 서버가 기존 값 유지) */
    skills?: { code: string; level: 'REQUIRED' | 'PREFERRED'; label?: string }[];
    /** 사용 차트 코드(DENTWEB|ETC), 없으면 null */
    chartProgram?: string | null;
    /** 사용 차트 직접 입력 이름(ETC 일 때만) */
    chartProgramEtc?: string | null;
}

interface ClinicType {
    description: string;
    type: string;
}

interface Station {
    id: string;
    name: string;
    lineNo: string;
}

export interface ClinicInfo {
    id: number;
    name: string;
    ownerName: string;
    managerName: string;
    managerEmail: string;
    managerPhoneNumber: string;
    description: string;
    totalWorkers: number;
    totalWorkersDescription: string;
    address: string;
    addressDetail: string;
    stations: Station[];
    url: string;
    pcThumbnail?: string;
    mobileThumbnail?: string;
    images?: string[];
    mobileImages?: string[];
    latitude: number;
    longitude: number;
    clinicStatusType: string;
    clinicType: ClinicType;
    /** 시/군/구 법정동 코드 — 수정 폼 지역 복원용 */
    districtId?: string | null;
    since?: string | null;
    /** 면허번호(예전 가입 칸) — 사업자등록번호가 아니다 */
    licenseNumber?: string | null;
    /** 사업자등록번호(숫자 10자리) */
    businessNumber?: string | null;
}

export interface ClinicFieldIssue {
    field: string;
    label: string;
    problem: 'MISSING' | 'INVALID';
    message: string;
}

/** 회원용 확인 기록 — 자동 판단(승인 여부·대조·판독)은 서버가 주지 않는다. 홈페이지에서 찾은 대표 사진 후보만 */
export interface ClinicVerification {
    checkedAt: string;
    thumbnailCandidates: string[];
    /** 후보와 같은 순서의 작은 미리보기(data URI) — null 이면 원래 주소로 */
    thumbnailPreviews: (string | null)[];
}

/** [홈페이지가 없어요]·올린 사업자등록증 — 사진 주소는 주지 않는다 */
export interface ClinicBusinessCertInfo {
    noHomepage: boolean;
    uploaded: boolean;
    uploadedAt?: string | null;
    /** 올린 뒤 30일이 지나 사진을 지웠는지 */
    imagePurged: boolean;
}

/** 사업자등록증 올리기 결과 — 저장·가입 요청에 token 을 넘긴다 */
export interface BusinessCertUploadResult {
    token: string;
    fileName?: string | null;
    mediaType: 'JPEG' | 'PNG' | 'PDF';
    expiresAt: string;
}

/** 병원 인증 — 확인된 병원 하나(고르면 다음 단계 칸을 미리 채운다) */
export interface SignupHospitalCandidate {
    name: string;
    address?: string | null;
    phone?: string | null;
    homepage?: string | null;
    kind?: string | null;
    /** 심평원 개설일 yyyyMMdd — 다음 단계 개원일 미리 채움 */
    establishedOn?: string | null;
    /** 가입 요청 clinic.hospitalCheckToken */
    token: string;
}

export interface SignupHospitalCheckResult {
    confirmed: boolean;
    /** 이름이 정확히 같고 고른 주소와 맞는 병원(보통 한 곳, 여럿이면 회원이 고른다) */
    candidates: SignupHospitalCandidate[];
    /** 심평원 결과가 너무 많아 못 찾음 — '병원 이름을 정확히' 안내 */
    tooMany?: boolean;
    /** 국세청 확인을 통과했는데 같은 치과로 쓰는 계정이 이미 있으면 — 화면이 가입 진행을 막고 안내한다(없으면 null) */
    existingAccount?: SignupExistingAccount | null;
}

/** 이미 가입된 치과 계정의 가입 방법 */
export type SignupExistingProvider = 'EMAIL' | 'KAKAO' | 'NAVER' | 'APPLE';

/** 병원 확인에서 찾은 이미 가입된 치과 계정(가린 값만) */
export interface SignupExistingAccount {
    /** 가린 로그인 이메일(예: br***@naver.com) */
    maskedEmail: string;
    provider: SignupExistingProvider;
    /** 가입 연월 "2025-03" */
    joinedYearMonth: string;
    clinicName: string;
}

/** 최종 치과 가입이 '이미 가입된 치과'로 막혔을 때 응답 code */
export const CLINIC_ALREADY_REGISTERED_CODE: number = 1222;

/** 가입 이메일 바꾸기 — 바꾸는 이유(선택) */
export type ClinicEmailChangeReason = 'MANAGER_CHANGED' | 'EMAIL_UNUSABLE' | 'OTHER';

/** `POST /auth/clinics/signup/email-change-requests` 요청 */
export interface ClinicEmailChangeRequestPayload {
    /** 가입 첫 단계에서 인증한 새 이메일 */
    email: string;
    businessNumber: string;
    ownerName: string;
    clinicName: string;
    address?: string;
    /** 병원 확인이 준 서명 토큰 — 있으면 서버가 국세청 확인을 거친 신청으로 기록 */
    hospitalToken?: string;
    /** 사업자등록증 업로드 토큰(`/auth/clinics/signup/business-cert`) — 필수 */
    businessCertToken: string;
    reason?: ClinicEmailChangeReason | null;
}

export interface ClinicEmailChangeRequestResult {
    id: number;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    createdAt: string;
    /** 국세청 확인을 거친 신청일 때만 */
    maskedOldEmail?: string | null;
}

export interface SignupHomepageCheckResult {
    confirmed: boolean;
    token?: string | null;
    addresses: string[];
    phones: string[];
}

/** 치과 가입 결과 */
export interface ClinicSignupResult {
    clinicVerified: boolean;
}

/** 치과 승인 준비 상태 — GET /clinics/me/readiness 등 */
export interface ClinicReadiness {
    clinicId: number;
    status: 'WAIT' | 'SERVICE' | 'DEFEAT' | 'DELETE';
    approved: boolean;
    issues: ClinicFieldIssue[];
    verification?: ClinicVerification | null;
    /** 옛 서버(배포 전)는 없다 */
    businessCert?: ClinicBusinessCertInfo | null;
    /** 회원에게 보일 부족한 점(빠진 칸 이름만) — 비었으면 '확인 중이에요' */
    memberReasons?: string[];
    /** 찾은 홈페이지 주소 — 홈페이지 칸이 비었을 때 제안만 */
    suggestedHomepage?: string | null;
}

/** 가입 전 홈페이지 미리보기 — 찾은 값은 제안만 */
export interface HomepagePreview {
    url: string;
    finalUrl?: string | null;
    failure?: string | null;
    ownerNames: string[];
    businessNumbers: string[];
    addresses: string[];
    phones: string[];
    thumbnailCandidates: string[];
    thumbnailPreviews: (string | null)[];
}

export interface ModifyClinicInfoRequest {
    name: string;
    since: string;
    phoneNumber: string;
    ownerName: string;
    managerName: string;
    managerEmail: string;
    managerPhoneNumber: string;
    description: string;
    totalWorkers: number;
    totalWorkersDescription: string;
    address: string;
    addressDetail: string;
    /** 호환용 — 서버는 addressCode 가 있으면 그것으로 지역을 판정한다 */
    districtId: string;
    /** 새로 고른 주소의 카카오 b_code 원문(10자리) */
    addressCode?: string;
    stationIds: string[];
    url: string;
    images?: string[];
    mobileImages?: string[];
    latitude: number;
    longitude: number;
    clinicType: string | undefined;
    /** 사업자등록번호 숫자 10자리(clinics.business_number) — 면허번호(licenseNumber)와 다른 칸 */
    businessNumber: string;
    /** [홈페이지가 없어요] — 보내지 않으면 지금 값 유지 */
    noHomepage?: boolean;
    /** 이번에 올린 사업자등록증 토큰 — 저장할 때 치과로 옮긴다 */
    businessCertToken?: string;
}

export interface ClinicsResponse {
    result: ClinicInfo;
    message: string;
    code: number;
}

export interface CareerInfoType {
    companyName: string;
    startDate: string | undefined;
    endDate: string | undefined;
    recruitmentType: string;
    jobDescription: string;    /** 재직 중 — true 면 종료일 없음 */
    currentlyEmployed?: boolean;
}

export interface ResumeRegisterType {
    id?: string;
    title: string;
    finalAcademicAbility?: string;
    finalAcademicAbilityStatus?: string;
    /** (사용 안 함) 옛 경력구분 — 서버가 무시한다 */
    careerType?: string;
    /** 연차(신입 0) — 저장하면 회원 정보의 연차가 바뀐다(저장하는 곳은 회원 정보 하나) */
    careerYears?: number;
    positionType?: string | null;
    careerInfos: CareerInfoType[];
    stationIds: string[];
    jobTaskTypes: string[];
    recruitmentTypes: string[];
    /** 옛 근무요일(옛 화면만) — 새 폼은 보내지 않는다 */
    workingDaysTypes?: string[];
    workingDays?: string;
    /** 희망 근무 요일 MON~SUN(공고와 같은 값) — 요일 1개 이상 또는 weekdayNegotiable 필수 */
    workWeekdays?: string[];
    saturdayMode?: string | null;
    weekdayNegotiable?: boolean;
    workDaysNote?: string;
    /** 가능한 날짜 yyyy-MM-dd — 아르바이트일 때만 */
    availableDates?: string[];
    selfIntroduction: string;
    certificateTypes: string[];
    currentSalary?: number | null;
    desiredSalary?: number | null;
    /** 할 수 있는 업무 코드 — 새 화면은 항상 보낸다(빈 배열이면 지움) */
    skills?: string[];
    /** 할 수 있는 업무 직접 입력(최대 3개·20자) */
    skillEtc?: string[];
    /** 써 본 차트 프로그램 코드(DENTWEB|ETC) */
    chartPrograms?: string[];
    /** 써 본 차트 직접 입력 이름(ETC 일 때만) */
    chartProgramEtc?: string | null;
  }

export interface Option {
    description: string;
    type: string;
}

export interface Meta {
    type: string;
    description: string;
    id?: string | number;
}

export interface WelfareOption extends Meta {
    customTitle?: string;
    customDescription?: string;
}

export interface Reply {
    id: string;
    content: string;
    createdAt: string;
    nickname: string;
    targetUserNickname?: string;
    isAuthor?: boolean;
    isExpert?: boolean;
    isWriter?: boolean;
    isDeleted?: boolean;
    likeCount?: number;
    liked?: boolean;
    /** 붙인 사진(속닥속닥) */
    imageUrl?: string | null;
}

export interface Comment {
    id: string;
    content: string;
    createdAt: string;
    nickname: string;
    isAuthor?: boolean;
    isExpert?: boolean;
    isWriter?: boolean;
    isDeleted?: boolean;
    likeCount?: number;
    liked?: boolean;
    /** 붙인 사진(속닥속닥) */
    imageUrl?: string | null;
    replies: Reply[];
}

export interface ResumeMetaDataResult {
    finalAcademicAbilityTypes: Option[];
    finalAcademicAbilityStatus: Option[];
    careerTypes: Option[];
    positionTypes?: Option[];
    jobTaskTypes: Option[];
    recruitmentTypes: Option[];
    workingDaysTypes: Option[];
    certificateTypes: Option[];
    /** 할 수 있는 업무 칩(공고와 같은 목록) */
    skills?: SkillMeta[];
    /** 써 본 차트 칩 — 덴트웹·직접 입력 */
    chartPrograms?: Option[];
    /** 업무 능력 상한(이력서는 etcMax·labelMaxLength 만) */
    skillLimits?: SkillLimits;
  }

export interface ResumeMetaType {
	code: number;
	message: string;
	result: ResumeMetaDataResult;
  }

export interface ApplicantInfo {
  userNo: string;
  name: string;
  /** 지원자 전화번호 — 그 지원 건 대화방 연락처가 열렸을 때만(면접 수락·근무 확정 뒤, 서버 ApplicantResumeAccess.canClinicSeePhone). 그 전엔 서버가 보내지 않는다 */
  phoneNumber?: string | null;
  email: string | null;
  birthDate: string | null;
  genderType: string;
  address: string;
  addressDetail: string | null;
}

export interface ResumeInfo {
  id: string;
  title: string;
  finalAcademicAbility: string;
  finalAcademicAbilityStatus: string;
  careerType: string;
    positionType?: string | null;
  careerInfos: CareerInfoType[];
  stationIds: string[];
  jobTaskTypes: string[];
  recruitmentTypes: string[];
  workingDaysTypes: string[];
  workingDays?: string;
  /** 희망 근무 요일 MON~SUN — 옛 이력서는 서버가 옛 근무요일을 바꿔 준다 */
  workWeekdays?: string[];
  /** EVERY|BIWEEKLY|MORNING — SAT 가 있을 때만 */
  saturdayMode?: string | null;
  weekdayNegotiable?: boolean;
  workDaysNote?: string | null;
  /** 가능한 날짜(오늘 이후만) */
  availableDates?: string[];
  selfIntroduction: string;
  certificateTypes: string[];
  currentSalary?: number | null;
  desiredSalary?: number | null;
  /** 할 수 있는 업무 [{description, type}](본인 입력) */
  skills?: Option[];
  /** 할 수 있는 업무 직접 입력 */
  skillEtc?: string[];
  /** 써 본 차트 표시명 */
  chartPrograms?: string[];
}

/** 치과가 보는 지원서 — 현재 상태·지원일·처리일·지원한 공고(서버 ApiApplicantResumeResponse.ApplicationInfo) */
export interface ApplicantApplicationInfo {
  applicationId: number;
  /** APPLY(지원완료)일 때만 합격/불합격 처리 가능 */
  applyStatus: 'APPLY' | 'PASS' | 'DROP' | 'CANCEL' | string;
  applyAt: string;
  /** 합격·불합격 처리 시각(기록된 건만) */
  resultAt?: string | null;
  recruitmentId?: number | null;
  recruitmentTitle: string;
  /** 이 지원 건의 대화방 단계(문자열) — 대화방이 없으면 null */
  stage?: string | null;
  /** 노쇼 이의 제기 여부 */
  stageDisputed?: boolean;
  /** 노쇼 이의 처리 결과(ACCEPTED·REJECTED) */
  disputeResolution?: string | null;
  /** 대화방 근무 조건 종류 — 단계 WITHDRAWN 이면 입사 포기(START·없음) / 근무 취소 */
  offerKind?: string | null;
}

/** 명치사 이력(건수만) — 명치사로 연결된 근무 완료·노쇼 횟수 */
export interface ApplicantRecord {
  worked: number;
  noShow: number;
  /** 약속 기록 공개(2단계) — 공개 스위치가 켜졌을 때만 온다. 없으면 "근무 완료 N · 노쇼 N" 그대로 */
  promise?: ApplicantPromisePublic | null;
}

/** 이 치과 지원 이력 한 줄(지금 보는 건 제외, 최신순) */
export interface SameClinicHistoryItem {
  applicationId: number;
  recruitmentId: number;
  title: string;
  appliedAt: string;
  status: 'APPLY' | 'PASS' | 'DROP' | 'CANCEL' | string;
  stage?: string | null;
  stageDisputed?: boolean;
  disputeResolution?: string | null;
  offerKind?: string | null;
  /** 이 지원 건 이력서를 볼 수 있는가(서버 판정) — false 면 줄을 이력서로 연결하지 않는다. 옛 서버(값 없음)는 연결 */
  resumeViewable?: boolean;
}

export interface ApplicantResumeViewType {
  applicant: ApplicantInfo;
  resume: ResumeInfo;
  /** 이 지원서 — 처리 여부는 목록에서 넘긴 값이 아니라 이 applyStatus 로 판단한다 */
  application?: ApplicantApplicationInfo | null;
  /** 면허 인증 배지 — 자격증 치과위생사 + 회원 면허 인증 */
  licenseVerified?: boolean;
  /** 이 공고 기준 업무 능력 일치 — 공고·이력서 둘 다 업무 능력이 있을 때만 */
  skillMatch?: SkillMatch | null;
  /** 이 공고 날짜와 겹치는 가능한 날 수 — 특정 날짜 공고이고 이력서에 가능한 날짜가 있을 때만 */
  availableDatesMatch?: number | null;
  /** 이 공고의 업무 능력(줄마다 matched) */
  recruitmentSkills?: RecruitmentSkillView[];
  /** 명치사 이력(건수만) — 집계 실패면 null */
  record?: ApplicantRecord | null;
  /** 이 치과 지원 이력 — 0건이면 블록을 숨긴다 */
  sameClinicHistory?: SameClinicHistoryItem[];
}

export interface ResumeViewType {
  id: string;
  title: string;
  finalAcademicAbility: string;
  finalAcademicAbilityStatus: string;
  /** (사용 안 함) 옛 경력구분 — 화면은 읽지 않는다 */
  careerType: string;
  /** 이력서 주인의 연차(회원 정보, 신입 0) — 미입력은 null */
  careerYears?: number | null;
    positionType?: string | null;
  careerInfos: CareerInfoType[];
  stationIds: string[];
  jobTaskTypes: string[];
  recruitmentTypes: string[];
  workingDaysTypes: string[];
  workingDays?: string;
  /** 희망 근무 요일 MON~SUN — 옛 이력서는 서버가 옛 근무요일을 바꿔 준다 */
  workWeekdays?: string[];
  /** EVERY|BIWEEKLY|MORNING — SAT 가 있을 때만 */
  saturdayMode?: string | null;
  weekdayNegotiable?: boolean;
  workDaysNote?: string | null;
  /** 가능한 날짜(오늘 이후만) */
  availableDates?: string[];
  selfIntroduction: string;
  certificateTypes: string[];
  currentSalary?: number | null;
  desiredSalary?: number | null;
  /** 할 수 있는 업무 [{description, type}] — 수정 폼 복원은 type */
  skills?: Option[];
  /** 할 수 있는 업무 직접 입력 */
  skillEtc?: string[];
  /** 써 본 차트 코드(DENTWEB|ETC) */
  chartPrograms?: string[];
  /** 써 본 차트 직접 입력 이름 */
  chartProgramEtc?: string | null;
  /** 써 본 차트 표시명 */
  chartProgramLabels?: string[];
  /** 면허 인증 배지 */
  licenseVerified?: boolean;
}

export interface ApplicantResumeViewResponse {
  code: number;
  message: string;
  result: ApplicantResumeViewType;
}

export interface ResumeViewResponse {
  code: number;
  message: string;
  result: ResumeViewType;
}

export interface SignupMeta {
    userTypes: Option[];
    positionTypes?: Option[];
    careerTypes?: Option[];
    clinicTypes?: Option[];
}

export interface SignupClinicMeta {
    clinicTypes: Option[];
}

// Discriminated union for home component list items
export type HomeComponentListItem = RecruitmentListItem | BannerListItem;

export interface RecruitmentListItem {
    isFavoriteClinic?: boolean;
    itemType: 'recruitment';
    continuousRecruitment?: boolean;
    id: number;
    title: string;
    description?: string;
    startDate: string;
    endDate: string;
    payAmount: string;
    clinic: {
        id: number;
        name: string;
        city?: string;
        district?: string;
        address?: string;
        stationName?: string;
        stations?: {
            id: string;
            name: string;
            lineNo: string;
        }[];
        pcThumbnail?: string;
        mobileThumbnail?: string;
        clinicStatusType?: string;
    };
    jobTasks?: string[];
    recruitmentTypes?: string[];
    recruitment?: string;
    position?: string;
    status: string;
    createAt: string;
    updateAt: string;
    welfares?: Array<{ description: string; type?: string }>;
}

export interface BannerListItem {
    itemType: 'banner';
    /** 배너 id — 홈 노출·클릭 기록용. 서버가 붙이기 전 응답(저장·복원된 홈)엔 없다 */
    id?: number | null;
    bannerUrl: string;
    mobileBannerUrl: string;
    endpointUrl: string;
}

export interface HomeRecruitmentList {
    premiumRows?: 1 | 2;
    /** 통합 홈에서 어드민 배치와 연결하는 안정적인 컴포넌트 ID. */
    componentId?: string;
    list: HomeComponentListItem[];
    title: string;
    type: 'LOCAL' | 'PREMIUM' | 'RECOMMEND' | 'BANNER';
    link?: string;
}

export interface HomeSearchResponse {
    userName?: string;
    topBanners?: string[];
    cities: { id: string; name: string }[];
    notices?: { id: number; title: string; noticeType: string }[];
    jobTasks: { description: string; type: string }[];
    subwayLines: { id: number; name: string }[];
    recruitments?: HomeRecruitmentList[];
}

// Enum 변환 함수들
export const getAcademicDescription = (type: string): string => {
    const academicTypes: { [key: string]: string } = {
        'HIGH_SCHOOL': '고등학교',
        'COLLEGE': '전문대학',
        'UNIVERSITY': '대학교',
        'MASTER': '대학원(석사)',
        'DOCTOR': '대학원(박사)'
    };
    return academicTypes[type] || type;
};

export const getStatusDescription = (type: string): string => {
    const statusTypes: { [key: string]: string } = {
        'GRADUATION': '졸업',
        'EXPECTED_GRADUATION': '졸업예정',
        'ATTENDING': '재학',
        'LEAVE_OF_ABSENCE': '휴학',
        'DROP_OUT': '중퇴'
    };
    return statusTypes[type] || type;
};

export const getCareerTypeDescription = (type: string): string => {
    return type === 'NEW_COMER' ? '신입' :
           type === 'CAREER_2_3' ? '2~3년' :
           type === 'CAREER_4_7' ? '4~7년' :
           type === 'CAREER_8_PLUS' ? '8년이상' :
           type === 'NONE' ? '무관' : type;
};

export const getCertificateDescription = (type: string): string => {
    const certTypes: { [key: string]: string } = {
        'DENTAL_NURSE': '치과간호사',
        'DENTAL_TECHNICIAN': '치과기공사',
        'DENTAL_TECH': '치과기공사',
        'DENTAL_HYGIENIST': '치과위생사'
    };
    return certTypes[type] || type;
};

export const getRecruitmentTypeDescription = (type: string): string => {
    const recruitmentTypes: { [key: string]: string } = {
        'FULL_TIME': '정규직',
        'PART_TIME': '파트타임',
        'CONTRACT': '계약직',
        'INTERN': '인턴'
    };
    return recruitmentTypes[type] || type;
};

export const getWorkingDaysDescription = (type: string): string => {
    const workingDaysTypes: { [key: string]: string } = {
        'FIVE_DAYS': '주 5일',
        'SIX_DAYS': '주 6일',
        'FLEXIBLE': '유연근무'
    };
    return workingDaysTypes[type] || type;
};

export const getJobTaskDescription = (type: string): string => {
    const jobTaskTypes: { [key: string]: string } = {
        'DENTAL_CARE': '치과진료',
        'ORTHODONTICS': '교정',
        'ORAL_SURGERY': '구강외과',
        'IMPLANT': '임플란트',
        'MEDICAL_OFFICE': '원무과',
        'DESK': '데스크'
    };
    return jobTaskTypes[type] || type;
};

export const getGenderDescription = (type: string | null | undefined): string => {
    if (!type) return ''; // 성별은 선택 입력 — 미입력이면 빈 값
    const genderTypes: { [key: string]: string } = {
        'MAN': '남성',
        'WOMAN': '여성'
    };
    return genderTypes[type] || type;
};

/** 강사 월 정산 — 결제 건별 내역 */
export interface InstructorSettlementPaymentLine {
    paidAt: string | null;
    amount: number;
    refundedAmount: number;
    status: string;          // DONE / CANCELED
    method: string | null;
    // ── 쿠폰(2026-09-28, docs/seminar-coupon-impl-plan.md 3절) — 구 서버 응답엔 없다 ──
    /** 참가자 이름(가림 처리된 "김OO") */
    participantName?: string | null;
    /** 정가 */
    listPrice?: number | null;
    couponName?: string | null;
    /** 쿠폰 할인액(0 이면 미사용) */
    discountAmount?: number | null;
    /** "강사" | "명치사" | "분담" */
    burdenLabel?: string | null;
    /** 이 결제의 명치사 부담 할인 */
    platformDiscountAmount?: number | null;
}

export type InstructorSettlementItemType = 'SEMINAR' | 'ADJUSTMENT' | 'CARRY' | 'MANUAL';

/** 정산서 한 줄 — 세미나 / 정산 후 환불 / 이월 / 조정 */
export interface InstructorSettlementItem {
    id: number | null;
    itemType: InstructorSettlementItemType;
    itemTypeLabel: string;
    seminarId: string | null;
    seminarTitle: string | null;
    basisAt: string | null;
    /** 서버가 만든 기준 문구 — 상시 동영상은 "상시 · 9월 결제분"(있으면 그대로 쓴다) */
    basisLabel?: string | null;
    paidCount: number;
    refundedCount: number;
    grossAmount: number;
    refundedAmount: number;
    netAmount: number;
    commissionAmount: number;
    instructorFeeCharge: number;
    payoutAmount: number;
    memo: string | null;
    payments: InstructorSettlementPaymentLine[];
    // ── 쿠폰 — 구 서버 응답엔 없다(새 서버는 쿠폰 없으면 0) ──
    /** 정가 합계 */
    listAmount?: number;
    /** 쿠폰 할인 합계(강사+명치사) */
    discountAmount?: number;
    /** 그중 명치사 부담분 — 정산 기준에 더해 준다 */
    platformDiscountAmount?: number;
    /** 그중 강사 부담분(내 쿠폰) */
    instructorDiscountAmount?: number;
    /** 정산 기준 합계(환불 전) — 환불 차감 후는 netAmount */
    settlementBasisAmount?: number;
    /** 쿠폰 사용 결제 수(상세에서만) */
    couponCount?: number | null;
}

export type InstructorSettlementStatus = 'CONFIRMING' | 'HOLD' | 'CONFIRMED' | 'PAID' | 'CARRIED_OVER';

/** 강사 월 정산서 */
export interface InstructorSettlement {
    id: number;
    settlementMonth: string;     // "2026-10"
    monthLabel: string;          // "2026년 10월"
    settlementBank: string | null;
    settlementAccount: string | null;   // 끝 4자리만
    settlementHolder: string | null;
    commissionRate: number;
    grossAmount: number;
    refundedAmount: number;
    netAmount: number;
    commissionAmount: number;
    instructorFeeCharge: number;
    adjustmentAmount: number;
    withholdingAmount: number;
    payoutAmount: number;
    status: InstructorSettlementStatus;
    statusLabel: string;
    objectionDeadline: string;
    canObject: boolean;
    objectionReason: string | null;
    objectionAt: string | null;
    resolveMemo: string | null;
    resolvedAt: string | null;
    payoutDate: string | null;
    expectedPayoutDate: string;     // 확정 전이면 예상 지급일
    paidAt: string | null;
    carriedToMonth: string | null;  // 이월 금액이 반영된 정산월 "2026-08"
    items: InstructorSettlementItem[];
    // ── 쿠폰 — 구 서버 응답엔 없다 ──
    listAmount?: number;
    discountAmount?: number;
    platformDiscountAmount?: number;
    instructorDiscountAmount?: number;
    settlementBasisAmount?: number;
}

/** 아직 정산서가 없는 정산 예정 세미나 */
export interface InstructorSettlementUpcoming {
    seminarId: string | null;
    seminarTitle: string | null;
    basisAt: string | null;
    /** 서버가 만든 기준 문구 — 상시 동영상은 "상시 · 9월 결제분"(있으면 그대로 쓴다) */
    basisLabel?: string | null;
    /** 세미나 일정 유형 — 상시 동영상이면 'OPEN'(서버가 주면) */
    schedule?: string | null;
    expectedStatementMonth: string;   // 이 세미나가 들어갈 정산서가 나오는 달 "2026-11"
    itemType: InstructorSettlementItemType;
    paidCount: number;
    netAmount: number;
    payoutAmount: number;
    memo: string | null;              // "폐강"
}

export interface InstructorSettlementOverview {
    upcoming: InstructorSettlementUpcoming[];
    settlements: InstructorSettlement[];
    hasAccount: boolean;
}

/* ───────────────────────── 세미나 쿠폰 (docs/seminar-coupon-impl-plan.md 3절) ───────────────────────── */

export type CouponDiscountType = 'FIXED' | 'RATE';
/** INSTRUCTOR 강사 쿠폰 / ADMIN 명치사 쿠폰 */
export type CouponIssuerType = 'INSTRUCTOR' | 'ADMIN';
/** "강사" | "명치사" | "분담" */
export type CouponBurdenLabel = string;

/** 세미나 상세 "쿠폰 N장" — 이 세미나에 받을 수 있는 쿠폰 */
export interface SeminarClaimableCoupon {
    couponId: number;
    name: string;
    discountType: CouponDiscountType;
    discountValue: number;
    maxDiscountAmount?: number | null;
    burdenLabel?: CouponBurdenLabel | null;
    issuerType?: CouponIssuerType;
    issuerName?: string | null;
    endsAt?: string | null;
    /** 남은 수량 — null 이면 무제한 */
    remaining?: number | null;
    soldOut?: boolean;
    /** "전체 유료 세미나" | 세미나 제목 */
    scopeLabel?: string | null;
    /** ALL 전체 / SEMINAR 지정 세미나 */
    scope?: 'ALL' | 'SEMINAR';
    /** 로그인했을 때만 의미가 있다 */
    claimed?: boolean;
    /** 받았으면 내 쿠폰함 id */
    issueId?: number | null;
    restoreOnRefund?: boolean;
    /** 이 세미나 정가 기준 할인액 */
    previewDiscount?: number | null;
}

/** 결제 모달 — 내 쿠폰 중 이 세미나에 쓸 수 있는지 */
export interface ApplicableCoupon {
    issueId: number;
    couponId?: number;
    name: string;
    discountAmount: number;
    applicable: boolean;
    /** "다른 세미나 전용" | "기간 지남" | "최소 정가 20,000원" */
    reason?: string | null;
    burdenLabel?: CouponBurdenLabel | null;
    issuerName?: string | null;
    restoreOnRefund?: boolean;
    endsAt?: string | null;
    /** "전체 유료 세미나" | 세미나 제목 */
    scopeLabel?: string | null;
    /** ALL 전체 / SEMINAR 지정 세미나 */
    scope?: 'ALL' | 'SEMINAR';
    issuerType?: CouponIssuerType;
    discountType?: CouponDiscountType;
    discountValue?: number;
    maxDiscountAmount?: number | null;
}

export interface ApplicableCouponsResponse {
    list?: ApplicableCoupon[];
    coupons?: ApplicableCoupon[];
    best?: number | null;
}

export type MyCouponStatus = 'AVAILABLE' | 'RESERVED' | 'USED' | 'EXPIRED';

/** 내 쿠폰함 한 장 */
export interface MyCoupon {
    issueId: number;
    name: string;
    discountType: CouponDiscountType;
    discountValue: number;
    maxDiscountAmount?: number | null;
    /** "전체 유료 세미나" | 세미나 제목 */
    scopeLabel?: string | null;
    /** ALL 전체 / SEMINAR 지정 세미나 */
    scope?: 'ALL' | 'SEMINAR';
    seminarId?: string | null;
    endsAt?: string | null;
    dday?: number | null;
    issuerType?: CouponIssuerType;
    issuerName?: string | null;
    burdenLabel?: CouponBurdenLabel | null;
    restoreOnRefund?: boolean;
    status: MyCouponStatus;
    usedSeminarTitle?: string | null;
    usedAt?: string | null;
}

export interface ClaimByCodeResult {
    issueId?: number;
    seminarId?: string | null;
    name?: string;
}

/** 결제 준비 응답(쿠폰 확장) */
export interface SeminarPaymentPrepareResult {
    orderId: string;
    amount: number;
    seminarTitle?: string;
    listAmount?: number;
    discountAmount?: number;
    couponName?: string | null;
}

export type InstructorCouponStatus = 'ACTIVE' | 'PAUSED' | 'EXPIRED';
export type InstructorCouponDistribution = 'CLAIM' | 'CODE';

/** 강사 세미나 폼 — 내가 만든 쿠폰 */
export interface InstructorCoupon {
    id: number;
    code: string;
    name: string;
    discountType: CouponDiscountType;
    discountValue: number;
    maxDiscountAmount?: number | null;
    startsAt?: string | null;
    endsAt?: string | null;
    maxIssues?: number | null;
    issuedCount: number;
    usedCount: number;
    distribution: InstructorCouponDistribution;
    restoreOnRefund: boolean;
    status: InstructorCouponStatus;
    /** 발급 0 이면 true — 값(할인 종류·금액)까지 고칠 수 있다 */
    editableValue?: boolean;
    /** 결제에 한 번도 안 쓰였으면 true. false 면 중지·재개만 되고 나머지는 잠김(서버 400) — 없으면 기존 동작 */
    editable?: boolean;
    /** 강사가 만든 쿠폰이면 true. 명치사가 만든 강사 부담·분담 쿠폰은 false(읽기 전용) */
    mine?: boolean;
    burdenType?: 'PLATFORM' | 'INSTRUCTOR' | 'SPLIT';
    burdenLabel?: string;
    /** 강사 분담률(강사 부담 1, 분담 0~1) */
    instructorShareRate?: number;
    /** 미리보기 — 이 세미나 정가 기준(서버 계산) */
    listPrice?: number;
    previewDiscount?: number;
    previewPrice?: number;
    /** 내 정산 기준(정가 − 할인 × 분담률) */
    settlementBasis?: number;
    /** 예상 지급(정산 기준 − 수수료) */
    expectedPayout?: number;
}

export interface InstructorCouponPayload {
    name: string;
    discountType: CouponDiscountType;
    discountValue: number;
    maxDiscountAmount?: number | null;
    startsAt?: string | null;
    endsAt?: string | null;
    maxIssues?: number | null;
    code?: string | null;
    distribution: InstructorCouponDistribution;
    restoreOnRefund: boolean;
    status?: InstructorCouponStatus;
}

/** 환불 견적의 쿠폰 정보 — 취소 팝업 "정가 − 쿠폰"·복원 안내 한 줄 */
export interface RefundQuoteCoupon {
    couponName: string;
    listAmount: number;
    discountAmount: number;
    /** 이 취소(환불율)로 쿠폰이 돌아오는지 */
    restored: boolean;
    /** "전액 환불이라 쿠폰은 다시 쓸 수 있어요." 등 */
    notice: string;
}

/** 세미나 등록하면서 만든 쿠폰 결과 — 요청 coupons[] 순서(index). error 가 있으면 그 쿠폰은 만들지 못했다(세미나는 저장됨) */
export interface SeminarCouponCreateResult {
    index: number;
    name: string;
    couponId?: number | null;
    error?: string | null;
}

/** 접속하기 — 최초 접속 시각을 기록하고 접속 정보를 돌려준다(동영상은 이 시각 이후 환불 불가) */
export interface SeminarOnlineAccessOpenResult {
    url: string;
    guide?: string | null;
    accessedAt?: string | null;
}

// ─── 채용 설문 + 홈 추천 24시간 (docs/recruitment-survey-boost-plan.md) ───────────────

/** 안내를 띄우지 않는 이유 — 서버 SurveyPromptBlockReason */
export type RecruitmentSurveyBlockReason = 'NOT_FOUND' | 'NOT_OWNER' | 'NOT_LIVE' | 'ALREADY_ANSWERED' | 'DISMISSED';

/** `GET /recruitments/{id}/survey-prompt` — card 는 홈 추천 카드와 같은 모양(이 치과 공고일 때만) */
export interface RecruitmentSurveyPrompt {
    show: boolean;
    reason: RecruitmentSurveyBlockReason | null;
    campaignKey: string;
    card: RecruitmentListItem | null;
}

/** `POST /recruitment-surveys` 본문 — 보기 코드는 서버 enum 이름, 기타(ETC)면 그 질문 *Etc 필수 */
export interface RecruitmentSurveyPayload {
    recruitmentId: number;
    likes: string[];
    pains: string[];
    wishes: string[];
    usage: string[];
    likesEtc?: string | null;
    painsEtc?: string | null;
    wishesEtc?: string | null;
    usageEtc?: string | null;
    comment?: string | null;
}

/** 제출 결과 — 시각은 KST 벽시계(`2026-10-07T15:20:00`) */
export interface RecruitmentSurveySubmitResult {
    surveyId: string;
    recruitmentId: number;
    campaignKey: string;
    boostStartAt: string;
    boostEndAt: string;
}
